import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import API_ENDPOINTS from "../ApiEndPoints";

const initial = {
  account: { account_name: "", email: "" },
  business_template: {
    title: "",
    description: "",
    properties: {
      logo_url: "",
      address1: "",
      address2: "",
      country: "",
      city: "",
      postcode: "",
      abn: "",
      acn: "",
      show_in_directory: 0,
      directory_description: "",
      products_services: "",
      problems_solved: "",
      service_locations: "",
      target_market_description: "",
    },
  },
  countries: [],
};

export default function BusinessSettings() {
  const [data, setData] = useState(initial);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [unsaved, setUnsaved] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [logoPreview, setLogoPreview] = useState("");
  const [banner, setBanner] = useState(null); // { text, type: 'info'|'success'|'danger' }
  const bannerTimer = useRef(null);

  const notify = (text, type = 'info', timeout = 2000) => {
    if (bannerTimer.current) {
      window.clearTimeout(bannerTimer.current);
    }
    setBanner({ text, type });
    bannerTimer.current = window.setTimeout(() => setBanner(null), timeout);
  };

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        setLoading(true);
        const res = await axios.get(API_ENDPOINTS.business.get);
        if (!mounted) return;
        const payload = res.data || {};
        const next = {
          account: payload.account || initial.account,
          business_template: {
            ...(payload.business_template || initial.business_template),
            properties: {
              ...initial.business_template.properties,
              ...((payload.business_template || {}).properties || {}),
            },
          },
          countries: payload.countries || [],
        };
        setData(next);
        const logo = next.business_template.properties.logo_url || "";
        setLogoPreview(logo);
      } finally {
        setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  const patch = (patchObj) => {
    setData((d) => ({ ...d, ...patchObj }));
    setUnsaved(true);
  };

  const patchAccount = (key, value) => {
    setData((d) => ({ ...d, account: { ...d.account, [key]: value } }));
    setUnsaved(true);
  };

  const patchBT = (key, value) => {
    setData((d) => ({ ...d, business_template: { ...d.business_template, [key]: value } }));
    setUnsaved(true);
  };

  const patchProp = (key, value) => {
    setData((d) => ({
      ...d,
      business_template: {
        ...d.business_template,
        properties: { ...d.business_template.properties, [key]: value },
      },
    }));
    setUnsaved(true);
  };

  const onCancel = async () => {
    // Reload from server
    setUnsaved(false);
    setLoading(true);
    const res = await axios.get(API_ENDPOINTS.business.get);
    const payload = res.data || {};
    const next = {
      account: payload.account || initial.account,
      business_template: {
        ...(payload.business_template || initial.business_template),
        properties: {
          ...initial.business_template.properties,
          ...((payload.business_template || {}).properties || {}),
        },
      },
      countries: payload.countries || [],
    };
    setData(next);
    setLogoPreview(next.business_template.properties.logo_url || "");
    setLoading(false);
  };

  const onSave = async () => {
    try {
      setSaving(true);
      await axios.post(API_ENDPOINTS.business.save, {
        account: {
          account_name: data.account.account_name,
          email: data.account.email,
        },
        business_template: {
          title: data.business_template.title,
          description: data.business_template.description,
          properties: data.business_template.properties,
        },
      });
      setUnsaved(false);
      notify("Settings Saved", "success");
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
      notify("Something went wrong trying to save your settings.", "danger");
    } finally {
      setSaving(false);
    }
  };

  const onLogoSelected = async (file) => {
    if (!file) return;
    try {
      setUploading(true);
      // 1) presign
      const presign = await axios.post(API_ENDPOINTS.business.logoPresign, {
        filename: file.name,
        size: file.size,
        mimetype: file.type,
      });
      const { key, presigned_put, storage_drive } = presign.data;
      // 2) PUT to S3
      await fetch(presigned_put, {
        method: "PUT",
        headers: { "Content-Type": file.type || "application/octet-stream" },
        body: file,
      });
      // 3) complete
      const done = await axios.post(API_ENDPOINTS.business.logoComplete, {
        key,
        storage_drive,
      });
      const logoUrl = done.data.logo_url;
      patchProp("logo_url", logoUrl);
      setLogoPreview(logoUrl);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
      notify("Image upload failed", "danger");
    } finally {
      setUploading(false);
    }
  };

  if (loading) return <div>Loading...</div>;

  const countries = data.countries || [];
  const props = data.business_template.properties || {};

  return (
    <div>
      {banner && (
        <div className={`alert alert-${banner.type || 'info'} m-b-10`}>
          {banner.text}
        </div>
      )}
      {!unsaved ? null : (
        <div className="row" style={{ marginBottom: "2em" }}>
          <div className="col-md-12 transition-all-ease-150">
            <button className="btn btn-primary m-r-10" onClick={onSave} disabled={saving}>
              {saving ? "Saving..." : "Save"}
            </button>
            <button className="btn btn-default" onClick={onCancel} disabled={saving}>
              Cancel
            </button>
          </div>
        </div>
      )}

      <div className="row">
        <div className="col-md-3">
          <div className="form-group"><h4>Account Details</h4></div>

          <div className="form-group">
            <label className="control-label">Account Name</label>
            <div className="do-field-margin">
              <input
                type="text"
                className="form-control"
                value={data.account.account_name || ""}
                onChange={(e) => patchAccount("account_name", e.target.value)}
              />
            </div>
          </div>

          <div className="form-group">
            <label className="control-label">Account Email</label>
            <div className="do-field-margin">
              <input
                type="text"
                className="form-control"
                value={data.account.email || ""}
                onChange={(e) => patchAccount("email", e.target.value)}
              />
            </div>
          </div>

          <div className="form-group">
            <label className="control-label">Name or Company Name:</label>
            <div className="do-field-margin">
              <input
                type="text"
                name="company_name"
                required
                className="form-control"
                value={data.business_template.title || ""}
                onChange={(e) => patchBT("title", e.target.value)}
              />
            </div>
          </div>

          <div className="form-group">
            <label className="control-label">Logo:</label>
            <div className="do-field-margin">
              {logoPreview ? (
                <a href="#" className="widget widget-card inverse-mode text-center" onClick={(e)=>e.preventDefault()}>
                  <div className="widget-card-cover">
                    <img src={logoPreview} alt="Business logo" />
                  </div>
                  <div className="widget-card-content full-height">
                    <div className="valign-middle-container">
                      <div className="valign-middle-inner-container"></div>
                    </div>
                  </div>
                </a>
              ) : null}

              <input
                id="logo_input"
                type="file"
                name="logo"
                className="form-control"
                accept="image/*"
                onChange={(e) => onLogoSelected(e.target.files[0])}
                style={{ display: "none" }}
              />
              <label htmlFor="logo_input" className="btn btn-sm btn-primary m-t-10">
                {uploading ? "Uploading..." : (logoPreview ? "Change" : "Upload")}
              </label>
            </div>
            <p>
              <sub>
                Please upload a logo as its used on both webpages and email templates, you can also change this later in the accounts section found in the top right drop down menu.
              </sub>
            </p>
          </div>
        </div>

        <div className="col-md-3">
          <div className="form-group">
            <label className="control-label">Address line 1:</label>
            <div className="do-field-margin">
              <input
                type="text"
                className="form-control"
                required
                value={props.address1 || ""}
                onChange={(e) => patchProp("address1", e.target.value)}
              />
            </div>
          </div>

          <div className="form-group">
            <label className="control-label">Address line 2 (optional):</label>
            <div className="do-field-margin">
              <input
                type="text"
                className="form-control"
                value={props.address2 || ""}
                onChange={(e) => patchProp("address2", e.target.value)}
              />
            </div>
          </div>

          <div className="form-group">
            <label className="control-label">Country:</label>
            <div className="do-field-margin">
              <select
                className="form-control"
                value={props.country || ""}
                onChange={(e) => patchProp("country", e.target.value)}
              >
                {(countries || []).map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="form-group">
            <label className="control-label">City:</label>
            <div className="do-field-margin">
              <input
                type="text"
                className="form-control"
                required
                value={props.city || ""}
                onChange={(e) => patchProp("city", e.target.value)}
              />
            </div>
          </div>

          <div className="form-group">
            <label className="control-label">Postcode:</label>
            <div className="do-field-margin">
              <input
                type="text"
                className="form-control"
                value={props.postcode || ""}
                onChange={(e) => patchProp("postcode", e.target.value)}
              />
            </div>
          </div>

          <div className="form-group">
            <label className="control-label">ABN:</label>
            <div className="do-field-margin">
              <input
                type="text"
                className="form-control"
                required
                value={props.abn || ""}
                onChange={(e) => patchProp("abn", e.target.value)}
              />
            </div>
          </div>

          <div className="form-group">
            <label className="control-label">ACN:</label>
            <div className="do-field-margin">
              <input
                type="text"
                className="form-control"
                value={props.acn || ""}
                onChange={(e) => patchProp("acn", e.target.value)}
              />
            </div>
          </div>

          <div className="form-group">
            <label className="control-label">Show on DO directory</label>
            <div className="do-field-margin">
              <select
                className="form-control m-b"
                value={String(props.show_in_directory ?? 0)}
                onChange={(e) => patchProp("show_in_directory", e.target.value)}
              >
                <option value="1">Yes</option>
                <option value="0">No</option>
              </select>
            </div>
          </div>
        </div>

        <div className="col-md-6">
          <div className="form-group">
            <label className="control-label">Description of your business:</label><br />
            <sub>When "Show on DO directory" is Yes this description will be shown</sub>
            <div className="do-field-margin">
              <textarea
                maxLength={2500}
                required
                className="form-control"
                value={props.directory_description || ""}
                onChange={(e) => patchProp("directory_description", e.target.value)}
              />
            </div>
          </div>

          <div className="form-group">
            <label className="control-label">Internal description of your business:</label><br />
            <sub>DO allows you to manage multiple business templates. Give this one a unique name.</sub>
            <div className="do-field-margin">
              <textarea
                maxLength={2500}
                required
                className="form-control"
                value={data.business_template.description || ""}
                onChange={(e) => patchBT("description", e.target.value)}
              />
            </div>
          </div>

          <div className="form-group">
            <label className="control-label">Products and services offer by your business:</label>
            <div className="do-field-margin">
              <textarea
                maxLength={2500}
                required
                className="form-control"
                value={props.products_services || ""}
                onChange={(e) => patchProp("products_services", e.target.value)}
              />
            </div>
          </div>

          <div className="form-group">
            <label className="control-label">Problem your business solves:</label>
            <div className="do-field-margin">
              <textarea
                maxLength={2500}
                required
                className="form-control"
                value={props.problems_solved || ""}
                onChange={(e) => patchProp("problems_solved", e.target.value)}
              />
            </div>
          </div>

          <div className="form-group">
            <label className="control-label">Location your business services:</label>
            <div className="do-field-margin">
              <textarea
                maxLength={2500}
                required
                className="form-control"
                value={props.service_locations || ""}
                onChange={(e) => patchProp("service_locations", e.target.value)}
              />
            </div>
          </div>

          <div className="form-group">
            <label className="control-label">Your ideal target market:</label>
            <div className="do-field-margin">
              <textarea
                maxLength={2500}
                required
                className="form-control"
                value={props.target_market_description || ""}
                onChange={(e) => patchProp("target_market_description", e.target.value)}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
